import { useCallback, useEffect, useState } from 'react';
import { AdSlot, AdsConsentNotice } from '../components/AdSlot';
import { Avatar } from '../components/Avatar';
import { Icon } from '../components/Icon';
import { MicIndicator } from '../components/MicIndicator';
import { MicMeter } from '../components/MicMeter';
import { Notice } from '../components/Notice';
import { NetIndicator } from '../components/NetIndicator';
import { PlayerGrid } from '../components/PlayerGrid';
import { Radar } from '../components/Radar';
import { Slider } from '../components/Slider';
import { StatusPill, describeStatus } from '../components/StatusPill';
import { teamColor, teamLabel } from '../components/team';
import { useDocumentHidden, useListenerYaw, usePeerAudio, usePrefs, useVoice } from '../hooks';
import { BRAND } from '../components/Logo';
import { keyLabel, savePrefs } from '../prefs';
import { navigate } from '../router';
import type { MicStatus, VoiceController, VoiceSnapshot } from '../voice/VoiceController';

/** Same breakpoint as the CSS (819px): two columns above, one column with the bottom bar below. */
const DESKTOP = '(min-width: 820px)';
const MOBILE = '(max-width: 819px)';
/** The 728x90 banner needs a players column of at least ~500px: not shown on narrow desktops (820-959px). */
const WIDE = '(min-width: 960px)';

const LEAVE_REASONS: Record<string, string> = {
  MC_OFFLINE: 'Sei uscito da Minecraft.',
  REVOKED: 'L’accesso alla voce è stato revocato.',
  SERVER_DOWN: 'Il server vocale non risponde. Riprovo appena possibile.',
  NO_SCOPE: 'Su questo server la voce non è disponibile.',
};

function micProblem(status: MicStatus): { title: string; text: string } | null {
  switch (status) {
    case 'denied':
      return {
        title: 'Microfono bloccato',
        text: 'Il browser non ha il permesso di usare il microfono. Clicca l’icona del lucchetto nella barra degli indirizzi, consenti il microfono e riprova. Intanto puoi ascoltare.',
      };
    case 'notfound':
      return { title: 'Nessun microfono trovato', text: 'Collega un microfono o delle cuffie con microfono e riprova. Intanto puoi ascoltare.' };
    case 'busy':
      return { title: 'Microfono occupato', text: 'Un’altra applicazione sta usando il microfono. Chiudila e riprova.' };
    case 'insecure':
      return { title: 'Connessione non sicura', text: 'Il microfono funziona solo su HTTPS (o su localhost). Apri la voice chat dall’indirizzo ufficiale.' };
    case 'error':
      return { title: 'Errore del microfono', text: 'Non riesco ad aprire il microfono. Riprova o scegline un altro.' };
    default:
      return null;
  }
}

/** "Sei su lobby2" (or the match + team), compact, on the Radar title row. */
function ScopeInfo({ s }: { s: VoiceSnapshot }) {
  if (s.mc !== 'online') {
    return (
      <span className="scope" data-testid="scope-info" data-mc="offline">
        <span className="scope-dim">Non sei in gioco</span>
      </span>
    );
  }
  const color = teamColor(s.teamId);
  return (
    <span className="scope" data-testid="scope-info" data-mc="online" data-scope-kind={s.scope?.kind ?? 'none'}>
      <span className="scope-kicker">{s.scope?.kind === 'match' ? 'In partita' : 'Sei su'}</span>{' '}
      <span className="scope-main">{s.scope?.label ?? s.server ?? '—'}</span>
      {s.teamId ? (
        <>
          {' '}
          <span className="chip" style={color ? { borderColor: color, color } : undefined} data-testid="team-info">
            Squadra {teamLabel(s.teamId)}
          </span>
        </>
      ) : null}
      {s.role === 'SPECTATOR' ? (
        <>
          {' '}
          <span className="chip chip-dim">Spettatore</span>
        </>
      ) : null}
    </span>
  );
}

export function VoicePage({ controller }: { controller: VoiceController }) {
  const s = useVoice(controller);
  const prefs = usePrefs();
  const audio = usePeerAudio(controller, 5);
  const yaw = useListenerYaw(controller, 10);
  const hidden = useDocumentHidden();
  const [wasHiddenInPtt, setWasHiddenInPtt] = useState(false);
  const [entering, setEntering] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const onHover = useCallback((id: string | null) => setHover(id), []);

  useEffect(() => {
    if (hidden && s.mic.mode === 'ptt' && s.entered) setWasHiddenInPtt(true);
  }, [hidden, s.mic.mode, s.entered]);

  useEffect(() => {
    const d = describeStatus(s);
    const mic = s.entered && s.phase === 'connected' ? (s.mic.deafened ? ' · Audio off' : s.mic.muted ? ' · Mutato' : '') : '';
    document.title = s.entered ? `${d.label}${mic} · ${BRAND.product}` : `${BRAND.product} · ${BRAND.domain}`;
  }, [s]);

  const enter = async () => {
    setEntering(true);
    try {
      await controller.enter();
    } finally {
      setEntering(false);
    }
  };

  const enableSpeakerMode = () => {
    savePrefs({ speakerMode: true });
    void controller.setSpeakerMode(true);
  };

  const mp = micProblem(s.mic.status);
  const ptt = s.mic.mode === 'ptt';
  const canTalk = s.mic.status === 'ok';
  const peers = s.entered ? s.peers : [];

  return (
    <main className="voice-layout" data-testid="voice-page">
      <aside className="side-panel" aria-label="Il tuo microfono">
        <section className="glass me-card">
          <div className="me-row">
            <span className={`me-head${s.selfSpeaking ? ' is-speaking' : ''}${s.entered && !s.mic.transmitting ? ' is-off' : ''}`}>
              {s.me ? <Avatar uuid={s.me.uuid} name={s.me.name} size={48} /> : null}
              {s.entered ? <NetIndicator net={s.selfNet?.net} via={s.selfNet?.via} testId="self-net" /> : null}
            </span>
            <div className="me-text">
              <span className="me-name">{s.me?.name ?? ''}</span>
              <div className="me-status">
                <StatusPill snapshot={s} />
              </div>
            </div>
            <MicIndicator s={s} pttKey={prefs.pttKey} />
          </div>

          {!s.entered ? (
            <div className="enter-block">
              <button type="button" className="btn btn-primary btn-enter" onClick={() => void enter()} disabled={entering || s.socket !== 'open'} data-testid="enter-button">
                <Icon name="headphones" size={20} />
                {entering ? 'Avvio…' : 'Entra in chat vocale'}
              </button>
            </div>
          ) : (
            <>
              {s.mc !== 'online' ? (
                <div className="offline-state" data-testid="mc-offline">
                  <strong>Entra in Minecraft per parlare.</strong>
                  <span className="muted small">
                    {s.leaveReason ? `${LEAVE_REASONS[s.leaveReason] ?? ''} ` : ''}Lascia aperta questa pagina: appena rientri la voce riparte da sola.
                  </span>
                </div>
              ) : s.leaveReason && !s.hasAssignment ? (
                <div className="offline-state">
                  <span className="muted small">{LEAVE_REASONS[s.leaveReason]}</span>
                </div>
              ) : null}
              <div className="big-buttons" role="toolbar" aria-label="Controlli voce">
                <button
                  type="button"
                  className={`big-btn${s.mic.muted || !canTalk ? ' is-off' : ''}`}
                  onClick={() => controller.toggleMute()}
                  aria-pressed={s.mic.muted}
                  data-testid="mute-button"
                  disabled={!canTalk}
                  title={s.mic.muted ? 'Riattiva il microfono' : 'Spegni il microfono'}
                >
                  <Icon name={s.mic.muted || !canTalk ? 'micOff' : 'mic'} size={18} />
                  <span>{s.mic.muted ? 'Riattiva' : 'Muto'}</span>
                </button>
                <button
                  type="button"
                  className={`big-btn${s.mic.deafened ? ' is-off' : ''}`}
                  onClick={() => controller.toggleDeafen()}
                  aria-pressed={s.mic.deafened}
                  data-testid="deafen-button"
                  title={s.mic.deafened ? 'Riattiva l’audio' : 'Disattiva l’audio'}
                >
                  <Icon name={s.mic.deafened ? 'headphonesOff' : 'headphones'} size={18} />
                  <span>{s.mic.deafened ? 'Audio off' : 'Audio'}</span>
                </button>
                <button
                  type="button"
                  className="big-btn big-settings"
                  onClick={() => navigate('/settings')}
                  aria-label="Impostazioni"
                  title="Impostazioni"
                  data-testid="open-settings"
                >
                  <Icon name="gear" size={18} />
                </button>
                {ptt ? (
                  <button
                    type="button"
                    className={`big-btn big-ptt${s.mic.pttPressed ? ' is-on' : ''}`}
                    onPointerDown={(e) => {
                      e.currentTarget.setPointerCapture(e.pointerId);
                      controller.setPushToTalk(true);
                    }}
                    onPointerUp={() => controller.setPushToTalk(false)}
                    onPointerCancel={() => controller.setPushToTalk(false)}
                    onContextMenu={(e) => e.preventDefault()}
                    data-testid="ptt-button"
                    disabled={!canTalk || s.mic.muted || s.mic.deafened}
                  >
                    <Icon name="keyboard" size={18} />
                    <span>Tieni {keyLabel(prefs.pttKey)}</span>
                  </button>
                ) : (
                  <button type="button" className="big-btn big-leave" onClick={() => void controller.exit()} data-testid="leave-button" title="Esci dalla voce">
                    <Icon name="door" size={18} />
                    <span>Esci</span>
                  </button>
                )}
              </div>
              <MicMeter controller={controller} active={s.mic.transmitting} />
              {ptt ? (
                <button type="button" className="btn btn-small btn-leave-ptt" onClick={() => void controller.exit()} data-testid="leave-button-ptt">
                  <Icon name="door" size={14} /> Esci dalla voce
                </button>
              ) : null}
            </>
          )}
        </section>

        <section className="glass radar-card" aria-label="Radar">
          <header className="card-title radar-title">
            <span className="radar-title-name">
              <Icon name="prox" size={14} /> Radar
            </span>
            <ScopeInfo s={s} />
          </header>
          <Radar peers={peers} audio={audio} yaw={yaw} gain={s.gain} mode={s.audioMode} highlight={hover} />
        </section>

        <section className="glass card volume-card">
          <header className="card-title">
            <Icon name="volume" size={14} /> Volume voci <output className="card-value">{Math.round(prefs.masterVolume * 100)}%</output>
          </header>
          <Slider
            min={0}
            max={120}
            value={Math.round(prefs.masterVolume * 100)}
            onChange={(v) => {
              savePrefs({ masterVolume: v / 100 });
              controller.setMasterVolume(v / 100);
            }}
            label="Volume voci"
            testId="master-volume"
          />
        </section>

        <div className="notices" aria-live="polite">
          {s.audioBlocked && s.entered ? (
            <Notice
              tone="warn"
              title="Audio in pausa"
              action={
                <button type="button" className="btn btn-small" onClick={() => void controller.resumeAudio()}>
                  Attiva audio
                </button>
              }
              testId="notice-audio-blocked"
            >
              Il browser ha sospeso l’audio di questa pagina.
            </Notice>
          ) : null}
          {s.howl.detected && s.entered ? (
            <Notice
              tone="danger"
              title="Rilevato fischio"
              testId="notice-howl"
              action={
                <>
                  <button type="button" className="btn btn-small" onClick={() => controller.resumeAfterHowl()} data-testid="howl-resume">
                    Riattiva microfono
                  </button>
                  {!prefs.speakerMode ? (
                    <button type="button" className="btn btn-small" onClick={enableSpeakerMode} data-testid="howl-speaker-mode">
                      Attiva modalità altoparlanti
                    </button>
                  ) : null}
                </>
              }
            >
              Rilevato fischio: abbassa il volume delle casse o usa le cuffie. Microfono disattivato.
              {!prefs.speakerMode ? ' Se usi le casse attiva la modalità altoparlanti.' : ''}
            </Notice>
          ) : null}
          {mp && s.entered ? (
            <Notice
              tone="danger"
              title={mp.title}
              testId="notice-mic"
              action={
                s.mic.status !== 'insecure' ? (
                  <button type="button" className="btn btn-small" onClick={() => void controller.retryMic()}>
                    Riprova
                  </button>
                ) : undefined
              }
            >
              {mp.text}
            </Notice>
          ) : null}
          {s.phase === 'failed' ? (
            <Notice tone="danger" title="Non riesco a collegarmi alla voce" testId="notice-failed">
              Il server riproverà da solo. Se il problema continua, la tua rete potrebbe bloccare il traffico vocale (UDP): prova un’altra rete.
            </Notice>
          ) : null}
          {ptt && wasHiddenInPtt ? (
            <Notice tone="warn" title="Push-to-talk e schede in background" onDismiss={() => setWasHiddenInPtt(false)} testId="notice-background">
              Il tasto {keyLabel(prefs.pttKey)} funziona solo quando questa scheda è in primo piano. Se giochi a schermo intero usa l’attivazione a voce.
            </Notice>
          ) : null}
          {!prefs.headphonesHintDismissed && !prefs.speakerMode ? (
            <Notice
              tone="danger"
              title="Usa le cuffie"
              onDismiss={() => savePrefs({ headphonesHintDismissed: true })}
              testId="notice-headphones"
              action={
                <button type="button" className="btn btn-small" onClick={enableSpeakerMode} data-testid="headphones-speaker-mode">
                  Uso le casse: attiva la modalità altoparlanti
                </button>
              }
            >
              Con gli altoparlanti gli altri sentono l’eco della propria voce. Nessuno registra: l’audio passa solo tra giocatori vicini.
            </Notice>
          ) : null}
          {!ptt && s.entered && !prefs.backgroundHintDismissed ? (
            <Notice
              tone="info"
              title="La Chat Vocale continua a funzionare anche se nascosta"
              testId="notice-background"
              action={
                <label className="check check-small">
                  <input type="checkbox" data-testid="notice-background-dismiss" onChange={(e) => e.target.checked && savePrefs({ backgroundHintDismissed: true })} />
                  <span>Non mostrare più</span>
                </label>
              }
            />
          ) : null}
          <AdsConsentNotice />
        </div>

        {/* desktop only, last in the panel, far from Muto/Esci: notices and controls always stay above it */}
        <AdSlot id="voice-sidebar" media={DESKTOP} />
      </aside>

      <section className="main-area">
        <PlayerGrid
          peers={peers}
          audio={audio}
          mode={s.audioMode}
          controller={controller}
          myTeam={s.teamId}
          onHover={onHover}
          emptyHint={
            !s.entered
              ? 'Entra in chat vocale per sentire gli altri giocatori.'
              : s.mc !== 'online'
                ? 'Nessuno: non sei in gioco.'
                : `Nessuno nel raggio di ${s.gain.hearRadius} blocchi. Avvicinati a qualcuno!`
          }
        />
        {/* desktop only, under "Connesso a N giocatori": the grid above never moves */}
        <AdSlot id="voice-bottom" media={WIDE} className="ad-voice-bottom" />
      </section>

      {/* mobile only: one banner at the very end of the content, above the bottom bar (never over it) */}
      <AdSlot id="voice-mobile" media={MOBILE} className="ad-voice-mobile" />
    </main>
  );
}
